/**
 * AddOnsSection Component
 *
 * Displays the user's active add-on bundles.
 * Part of the "My Access" page for all users.
 */

import { Extension } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import type { ActiveBundleResponse } from '@store/generated/subscriptions';

interface AddOnsSectionProps {
  addOns: ActiveBundleResponse[];
  isLoading?: boolean;
}

export function AddOnsSection({ addOns, isLoading }: AddOnsSectionProps) {
  if (isLoading) {
    return (
      <Box component="section" sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Add-ons
        </Typography>
        <Stack spacing={2}>
          {[1, 2].map(i => (
            <Card elevation={1} key={i}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="30%" height={24} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  if (addOns.length === 0) {
    return (
      <Box component="section" sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Add-ons
        </Typography>
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Extension sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No add-ons active
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add-ons provide additional features to enhance your subscription.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Add-ons
      </Typography>
      <Stack spacing={2}>
        {addOns.map(addOn => (
          <Card
            elevation={1}
            key={addOn.assignment_id}
            sx={{
              border: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Extension sx={{ color: 'primary.main', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {addOn.bundle_name}
                  </Typography>
                  {addOn.bundle_description && (
                    <Typography variant="body2" color="text.secondary">
                      {addOn.bundle_description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
